/**
 * Hooks para gestión de progreso y logros
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  achievementService,
  classProgressService,
  subscribeToAchievementsByClub,
  subscribeToAchievementsByPerson,
  subscribeToAchievementsByType,
  subscribeToAchievement,
  subscribeToClassProgressByClub,
  subscribeToClassProgressByPerson,
  subscribeToActiveClassProgress,
  subscribeToClassProgress,
} from "@/lib/services/progress";
import { useAuthStore } from "@/stores/authStore";
import type { Achievement, ClassProgress } from "@/types";
import { useRealtimeList, useRealtimeDoc } from "@/hooks/useRealtime";

// ==================== QUERY KEYS ====================

export const achievementKeys = {
  all: ["achievements"] as const,
  byPerson: (personId: string) => ["achievements", "person", personId] as const,
  byClub: (clubId: string, periodId?: string) =>
    periodId
      ? (["achievements", "club", clubId, periodId] as const)
      : (["achievements", "club", clubId] as const),
  byType: (personId: string, type: string) =>
    ["achievements", "person", personId, type] as const,
  detail: (id: string) => ["achievements", id] as const,
};

export const classProgressKeys = {
  all: ["classProgress"] as const,
  byPerson: (personId: string) =>
    ["classProgress", "person", personId] as const,
  byClub: (clubId: string, periodId?: string) =>
    periodId
      ? (["classProgress", "club", clubId, periodId] as const)
      : (["classProgress", "club", clubId] as const),
  active: (personId: string) => ["classProgress", "active", personId] as const,
  detail: (id: string) => ["classProgress", id] as const,
};

// ==================== ACHIEVEMENT HOOKS ====================

export function useAchievementsByPerson(personId: string) {
  return useRealtimeList<Achievement>(
    personId
      ? (cb, onErr) => subscribeToAchievementsByPerson(personId, cb, onErr)
      : null,
    [personId],
  );
}

export function useAchievementsByClub(clubId: string, periodId?: string) {
  return useRealtimeList<Achievement>(
    clubId
      ? (cb, onErr) =>
          subscribeToAchievementsByClub(clubId, periodId, cb, onErr)
      : null,
    [clubId, periodId],
  );
}

export function useAchievementsByType(
  personId: string,
  type: Achievement["type"],
) {
  return useRealtimeList<Achievement>(
    personId && type
      ? (cb, onErr) => subscribeToAchievementsByType(personId, type, cb, onErr)
      : null,
    [personId, type],
  );
}

export function useAchievement(id: string) {
  return useRealtimeDoc<Achievement>(
    id ? (cb, onErr) => subscribeToAchievement(id, cb, onErr) : null,
    [id],
  );
}

export function useCreateAchievement() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (
      data: Omit<
        Achievement,
        "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
      >,
    ) => achievementService.create(data as any, user?.uid || ""),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({
        queryKey: achievementKeys.byPerson(data.personId),
      });
      queryClient.invalidateQueries({
        queryKey: achievementKeys.byClub(data.clubId),
      });
      toast.success("Logro registrado exitosamente");
    },
    onError: (error) => {
      console.error("Error creating achievement:", error);
      toast.error("Error al registrar el logro");
    },
  });
}

export function useUpdateAchievement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Achievement> }) =>
      achievementService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: achievementKeys.all });
      toast.success("Logro actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el logro");
    },
  });
}

export function useRevokeAchievement() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      achievementService.revoke(id, reason, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: achievementKeys.all });
      toast.success("Logro revocado");
    },
    onError: () => {
      toast.error("Error al revocar el logro");
    },
  });
}

export function useDeleteAchievement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => achievementService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: achievementKeys.all });
      toast.success("Logro eliminado");
    },
    onError: () => {
      toast.error("Error al eliminar el logro");
    },
  });
}

// ==================== CLASS PROGRESS HOOKS ====================

export function useClassProgressByPerson(personId: string) {
  return useRealtimeList<ClassProgress>(
    personId
      ? (cb, onErr) => subscribeToClassProgressByPerson(personId, cb, onErr)
      : null,
    [personId],
  );
}

export function useClassProgressByClub(clubId: string, periodId?: string) {
  return useRealtimeList<ClassProgress>(
    clubId
      ? (cb, onErr) =>
          subscribeToClassProgressByClub(clubId, periodId, cb, onErr)
      : null,
    [clubId, periodId],
  );
}

export function useActiveClassProgress(personId: string) {
  return useRealtimeList<ClassProgress>(
    personId
      ? (cb, onErr) => subscribeToActiveClassProgress(personId, cb, onErr)
      : null,
    [personId],
  );
}

export function useClassProgress(id: string) {
  return useRealtimeDoc<ClassProgress>(
    id ? (cb, onErr) => subscribeToClassProgress(id, cb, onErr) : null,
    [id],
  );
}

export function useCreateClassProgress() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (
      data: Omit<
        ClassProgress,
        "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
      >,
    ) => classProgressService.create(data as any, user?.uid || ""),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({
        queryKey: classProgressKeys.byPerson(data.personId),
      });
      queryClient.invalidateQueries({
        queryKey: classProgressKeys.byClub(data.clubId),
      });
      toast.success("Clase iniciada exitosamente");
    },
    onError: (error: Error) => {
      console.error("Error creating class progress:", error);
      // Mostrar mensaje específico si ya existe el progreso
      toast.error(error.message || "Error al iniciar la clase");
    },
  });
}

export function useUpdateClassProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ClassProgress> }) =>
      classProgressService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classProgressKeys.all });
      toast.success("Progreso actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el progreso");
    },
  });
}

export function useUpdateRequirement() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: ({
      progressId,
      requirementId,
      completed,
    }: {
      progressId: string;
      requirementId: string;
      completed: boolean;
    }) =>
      classProgressService.updateRequirement(
        progressId,
        requirementId,
        completed,
        user?.uid || "",
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classProgressKeys.all });
    },
    onError: () => {
      toast.error("Error al actualizar el requisito");
    },
  });
}

export function useCompleteClass() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (id: string) =>
      classProgressService.markCompleted(id, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classProgressKeys.all });
      toast.success("Clase marcada como completada");
    },
    onError: () => {
      toast.error("Error al completar la clase");
    },
  });
}

export function useInvestClass() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (id: string) =>
      classProgressService.markInvested(id, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classProgressKeys.all });
      toast.success("Investidura registrada");
    },
    onError: () => {
      toast.error("Error al registrar la investidura");
    },
  });
}

// ==================== FLUJO DE APROBACIÓN ====================

export function useSubmitForApproval() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (id: string) =>
      classProgressService.submitForApproval(id, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classProgressKeys.all });
      toast.success("Clase enviada para aprobación");
    },
    onError: (error) => {
      console.error("Error submitting for approval:", error);
      toast.error("Error al enviar para aprobación");
    },
  });
}

export function useApproveClass() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (id: string) =>
      classProgressService.approveClass(id, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classProgressKeys.all });
      toast.success("Clase aprobada exitosamente");
    },
    onError: (error) => {
      console.error("Error approving class:", error);
      toast.error("Error al aprobar la clase");
    },
  });
}

export function useRejectClass() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      classProgressService.rejectClass(id, user?.uid || "", reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classProgressKeys.all });
      toast.success("Clase rechazada");
    },
    onError: (error) => {
      console.error("Error rejecting class:", error);
      toast.error("Error al rechazar la clase");
    },
  });
}

export function usePendingApprovals(associationId: string) {
  return useQuery({
    queryKey: ["classProgress", "pendingApproval", associationId],
    queryFn: () =>
      classProgressService.getPendingApprovalByAssociation(associationId),
    enabled: !!associationId,
  });
}

// Hook para obtener progresos en curso de una clase específica
export function useClassProgressByClubAndClass(
  clubId: string,
  classId: string,
) {
  return useQuery({
    queryKey: ["classProgress", "byClubAndClass", clubId, classId],
    queryFn: () => classProgressService.getByClubAndClass(clubId, classId),
    enabled: !!clubId && !!classId,
  });
}

// Hook para sincronizar requisitos a progresos existentes
export function useSyncRequirementsToExisting() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: ({
      clubId,
      classId,
      requirements,
    }: {
      clubId: string;
      classId: string;
      requirements: {
        id: string;
        name: string;
        description?: string;
        order: number;
      }[];
    }) =>
      classProgressService.syncRequirementsToExisting(
        clubId,
        classId,
        requirements,
        user?.uid || "",
      ),
    onSuccess: (updatedCount) => {
      queryClient.invalidateQueries({ queryKey: classProgressKeys.all });
      if (updatedCount > 0) {
        toast.success(
          `Se actualizaron ${updatedCount} progreso(s) existente(s)`,
        );
      }
    },
    onError: (error) => {
      console.error("Error syncing requirements:", error);
      toast.error("Error al sincronizar requisitos");
    },
  });
}
